
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Edit, Trash2, Calendar } from "lucide-react";
import { BlogPost, BlogCategory } from "@/types/blogPost";
import { fetchBlogCategories } from "@/utils/blogOperations";

interface BlogPostTableProps {
  posts: BlogPost[];
  onEdit: (post: BlogPost) => void;
  onDelete: (postId: string) => void;
}

export function BlogPostTable({ posts, onEdit, onDelete }: BlogPostTableProps) {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(posts);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    setFilteredPosts(posts);
  }, [posts]);

  useEffect(() => {
    applyFilters();
  }, [posts, categoryFilter, statusFilter, startDate, endDate, searchTerm]);

  const loadCategories = async () => {
    try {
      const data = await fetchBlogCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...posts];

    // Filtro por categoria
    if (categoryFilter !== "all") {
      filtered = filtered.filter(post => post.category_id === categoryFilter);
    }

    // Filtro por status de publicação
    if (statusFilter !== "all") {
      const isPublished = statusFilter === "published";
      filtered = filtered.filter(post => post.published === isPublished);
    }

    // Filtro por período de data
    if (startDate) {
      filtered = filtered.filter(post => {
        if (!post.created_at) return false;
        return new Date(post.created_at) >= new Date(startDate);
      });
    }

    if (endDate) {
      filtered = filtered.filter(post => {
        if (!post.created_at) return false;
        return new Date(post.created_at) <= new Date(endDate);
      });
    }

    // Filtro por termo de busca
    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.subtitle && post.subtitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
        post.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPosts(filtered);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return 'Outras';
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || 'Outras';
  };

  const getCategoryColor = (categoryId: string | null) => {
    if (!categoryId) return { color_hex: '#6B7280', border_color_hex: '#D1D5DB', text_color_hex: '#000000' };
    const category = categories.find(cat => cat.id === categoryId);
    return category || { color_hex: '#6B7280', border_color_hex: '#D1D5DB', text_color_hex: '#000000' };
  };

  const clearFilters = () => {
    setCategoryFilter("all");
    setStatusFilter("all");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
  };

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="font-semibold text-gray-800">Filtros</h4>
          <Button onClick={clearFilters} variant="outline" size="sm">
            Limpar Filtros
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Busca por texto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <Input
              placeholder="Título, subtítulo ou conteúdo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Filtro por categoria */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoria
            </label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todas as categorias" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro por status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="published">Publicado</SelectItem>
                <SelectItem value="draft">Rascunho</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Data inicial */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Inicial
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          {/* Data final */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Final
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Data de Criação</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPosts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  Nenhum post encontrado com os filtros aplicados.
                </TableCell>
              </TableRow>
            ) : (
              filteredPosts.map((post) => {
                const categoryColors = getCategoryColor(post.category_id);
                return (
                  <TableRow key={post.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium text-gray-900">
                          {post.title}
                        </div>
                        {post.subtitle && (
                          <div className="text-sm text-gray-500">
                            {post.subtitle}
                          </div>
                        )}
                        {post.is_featured && (
                          <Badge variant="default" className="text-xs bg-yellow-500 mt-1">
                            Destaque
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        style={{ 
                          backgroundColor: categoryColors.color_hex,
                          borderColor: categoryColors.border_color_hex,
                          color: categoryColors.text_color_hex
                        }}
                        className="border text-xs"
                      >
                        {getCategoryName(post.category_id)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={post.published ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {post.published ? "Publicado" : "Rascunho"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        {post.created_at ? formatDate(post.created_at) : "N/A"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => onEdit(post)}
                          size="sm"
                          variant="outline"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => onDelete(post.id)}
                          size="sm"
                          variant="destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Resumo dos resultados */}
      <div className="text-sm text-gray-600">
        Mostrando {filteredPosts.length} de {posts.length} posts
      </div>
    </div>
  );
}
